import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "../lib/firebase";

const MIN_LENGTH = 8;

/**
 * Firebase itself accepts 6 characters. These accounts hold a hall's bookings
 * and ledger, so the panel asks for 8 — the check is client-side and advisory,
 * not a security boundary.
 */
function localValidation(current: string, next: string, repeat: string): string | null {
  if (next.length < MIN_LENGTH) return `رمز جدید باید حداقل ${MIN_LENGTH} حرف باشد`;
  if (next !== repeat) return "رمز جدید و تکرار آن یکسان نیستند";
  if (next === current) return "رمز جدید با رمز فعلی تفاوتی ندارد";
  return null;
}

/** Firebase auth codes → a sentence the hall owner can act on. */
function messageFor(error: unknown): string {
  const code = error instanceof FirebaseError ? error.code : "";
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "رمز فعلی درست نیست";
    case "auth/weak-password":
      return "رمز جدید بسیار ساده است — حرف و رقم را با هم بیامیزید";
    case "auth/too-many-requests":
      return "تلاش زیاد شد — چند دقیقه صبر کنید و باز امتحان کنید";
    case "auth/requires-recent-login":
      return "نشست شما کهنه شده — یک بار خارج و دوباره وارد شوید";
    case "auth/network-request-failed":
      return "اتصال برقرار نشد — انترنت را بررسی کنید";
    default:
      return "تغییر رمز ناکام شد — دوباره امتحان کنید";
  }
}

/**
 * Change the signed-in user's own password. Firebase requires a fresh
 * credential before a password change, so the current password is
 * re-authenticated first and only then replaced.
 */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function close() {
    setCurrent("");
    setNext("");
    setRepeat("");
    setReveal(false);
    setError(null);
    setDone(false);
    onClose();
  }

  async function submit() {
    const user = auth.currentUser;
    if (!user?.email) {
      setError("این حساب با ایمیل وارد نشده — تغییر رمز از اینجا ممکن نیست");
      return;
    }
    const invalid = localValidation(current, next, repeat);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      setDone(true);
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(false);
    }
  }

  const revealToggle = (
    <InputAdornment position="end">
      <IconButton onClick={() => setReveal((v) => !v)} edge="end" size="small">
        {reveal ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="xs" fullWidth>
      <DialogTitle>تغییر رمز عبور</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            رمز عبور عوض شد. دفعهٔ بعد با رمز جدید وارد شوید.
          </Alert>
        ) : (
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              برای امنیت، اول رمز فعلی را بنویسید.
            </Typography>
            <TextField
              label="رمز فعلی"
              type={reveal ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              fullWidth
              autoFocus
              InputProps={{ endAdornment: revealToggle }}
            />
            <TextField
              label="رمز جدید"
              type={reveal ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              fullWidth
              helperText={`حداقل ${MIN_LENGTH} حرف`}
            />
            <TextField
              label="تکرار رمز جدید"
              type={reveal ? "text" : "password"}
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              autoComplete="new-password"
              fullWidth
              error={repeat.length > 0 && repeat !== next}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !busy) submit();
              }}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={close}>
            بستن
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              انصراف
            </Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={busy || !current || !next || !repeat}
            >
              {busy ? "در حال تغییر…" : "تغییر رمز"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
